/**
 * /auth/reset — request a password reset. Phase 3b of ottotester#65.
 *
 * Calls Better Auth's `requestPasswordReset` which (via Phase 3a's
 * `sendResetPassword` callback in betterAuth.ts) sends an email
 * containing a link to /auth/reset-confirm?token=... — the next screen.
 * (Older Better Auth versions exposed this as `forgetPassword`; v1.6
 * renamed it.)
 *
 * Always shows the same "if an account exists, we sent an email"
 * message regardless of whether the address is registered, so an
 * attacker can't enumerate which addresses have accounts.
 *
 * When this installation can't send email there is no form: a reset
 * email would never arrive. The screen says so and points the person to
 * whoever runs the server, who can make them a reset link with the
 * server's reset command.
 */
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { MailCheck, Send } from 'lucide-react'

import { Button, Input, Label } from '@/components/ui'
import { authClient } from '@/auth/authClient'
import { AuthLayout } from '@/auth/AuthLayout'
import { useEmailStatus } from '@/hooks/usePublicSettings'

export function RequestPasswordReset() {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const emailStatus = useEmailStatus()

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: '/auth/reset-confirm',
    })
    setSubmitting(false)
    if (error) {
      // Surface only generic errors. Don't reveal whether the email
      // exists (Better Auth typically returns success regardless, but
      // belt-and-suspenders).
      setError(error.message ?? 'Could not send the reset link. Try again.')
      return
    }
    setSubmitted(true)
  }

  if (emailStatus === 'off') {
    return (
      <AuthLayout
        title="Reset your password"
        subtitle="This installation can't send email, so it can't send you a reset link."
        footer={
          <Link to="/auth/login" className="text-primary hover:underline font-medium">
            Back to sign in
          </Link>
        }
      >
        <p className="text-sm text-muted-foreground text-center py-2">
          Ask whoever runs OttoTester here to make you a reset link.
        </p>
      </AuthLayout>
    )
  }

  if (submitted) {
    return (
      <AuthLayout
        title="Check your inbox"
        subtitle={`If an account exists for ${email}, we just sent a reset link.`}
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <MailCheck className="h-10 w-10 text-primary" aria-hidden="true" />
          <p className="text-sm text-muted-foreground text-center">
            The link expires in 1 hour. If it doesn't arrive in a few minutes, check your spam folder.
          </p>
          <Link to="/auth/login" className="text-sm text-primary hover:underline">
            Back to sign in
          </Link>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="Enter your email — we'll send a link to set a new password."
      footer={
        <Link to="/auth/login" className="text-primary hover:underline font-medium">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-3" aria-label="Request password reset form">
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <Button type="submit" disabled={submitting || emailStatus === 'loading'} className="w-full">
          <Send className="h-4 w-4 mr-2" />
          {submitting ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </AuthLayout>
  )
}
